import { useState } from 'react';
import { Check, Copy, ArrowUpRight, LockKeyhole } from 'lucide-react';
import { Button } from 'components/ui/button';

export function ReadAccess() {
    return (
        <div className="mcp-permissions">
            {[
                'Organizations & events',
                'Online & onsite orders',
                'Customer names, email & phone',
                'Sales totals',
            ].map((item) => (
                <span key={item}>
                    <Check size={14} aria-hidden="true" />
                    {item}
                </span>
            ))}
        </div>
    );
}

export function CopyButton({ value, label = 'Copy', onError }) {
    const [copied, setCopied] = useState(false);
    async function copy() {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
        } catch {
            onError('Clipboard unavailable. Select and copy the value manually.');
        }
    }
    return (
        <Button variant="outline" size="sm" onClick={copy} aria-label={label}>
            {copied ? <Check /> : <Copy />}
            {copied ? 'Copied' : label}
        </Button>
    );
}

export function ConnectionSetup({ endpoint, onError }) {
    const [guide, setGuide] = useState('oauth');
    return (
        <aside className="mcp-aside">
            <section className="mcp-panel mcp-setup">
                <div className="mcp-section-heading">
                    <div>
                        <span className="mcp-eyebrow">GET CONNECTED</span>
                        <h2>One URL. Your AI app.</h2>
                    </div>
                </div>
                <p>Connect Frontdoor using a remote MCP server.</p>
                <label htmlFor="mcp-endpoint">Server URL</label>
                <div className="mcp-endpoint">
                    <input
                        id="mcp-endpoint"
                        readOnly
                        value={endpoint || 'Available after server setup'}
                    />
                    {endpoint && <CopyButton value={endpoint} label="Copy URL" onError={onError} />}
                </div>
                <div className="mcp-guide-tabs" aria-label="Connection method">
                    <button
                        type="button"
                        aria-pressed={guide === 'oauth'}
                        onClick={() => setGuide('oauth')}
                    >
                        Dots & Grok
                    </button>
                    <button
                        type="button"
                        aria-pressed={guide === 'key'}
                        onClick={() => setGuide('key')}
                    >
                        Access key
                    </button>
                </div>
                {guide === 'oauth' ? (
                    <ol className="mcp-steps">
                        <li>
                            <strong>Add a custom MCP connection</strong>
                            <span>
                                In ChatGPT’s app settings or Grok’s connectors, enter the server
                                URL.
                            </span>
                        </li>
                        <li>
                            <strong>Choose OAuth</strong>
                            <span>
                                Use dynamic registration with PKCE. No client secret is needed.
                            </span>
                        </li>
                        <li>
                            <strong>Approve in Frontdoor</strong>
                            <span>
                                Sign in as an internal admin, review the access, and name your
                                connection.
                            </span>
                        </li>
                    </ol>
                ) : (
                    <ol className="mcp-steps">
                        <li>
                            <strong>Create a dedicated access key</strong>
                            <span>Give each bot its own name and expiry.</span>
                        </li>
                        <li>
                            <strong>Set the authorization header</strong>
                            <code>Authorization: Bearer &lt;key&gt;</code>
                        </li>
                        <li>
                            <strong>Use Streamable HTTP</strong>
                            <span>
                                Add the server URL to a client that supports remote MCP and custom
                                headers.
                            </span>
                        </li>
                    </ol>
                )}
                <div className="mcp-doc-links">
                    <a
                        href="https://developers.openai.com/plugins/build/auth"
                        target="_blank"
                        rel="noreferrer"
                    >
                        ChatGPT setup
                        <ArrowUpRight size={13} />
                    </a>
                    <a href="https://docs.x.ai/grok/connectors" target="_blank" rel="noreferrer">
                        Grok setup
                        <ArrowUpRight size={13} />
                    </a>
                </div>
            </section>
            <section className="mcp-access">
                <LockKeyhole size={20} />
                <h2>Context, with boundaries.</h2>
                <p>All connections can read these areas across every organization.</p>
                <ReadAccess />
                <div className="mcp-access-rule">
                    No edits, refunds, deletions or messages. Every tool is enforced as read only by
                    the server.
                </div>
                <p className="mcp-fine-print">
                    Pausing stops new reads and discards pending results. Data already received by
                    an app cannot be recalled.
                </p>
            </section>
        </aside>
    );
}
